import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { canWriteAnkhCustomers } from "../../components/nav";
import {
  ANKH_SELLER,
  GSTIN_RE,
  MAX_LINE_NOTE,
  addInvoiceLine,
  createDraftInvoice,
  customerState,
  getAnkhInvoice,
  listAnkhCustomers,
  openCompanyAccount,
  openOperatorAccount,
  periodEnd,
  priceNeedsReason,
  setInvoiceStatus,
  type AnkhCustomer,
  DEFAULT_CUSTOMER_TYPES,
  type AnkhCustomerType,
  type CompanyAccountInput,
} from "../../api/ankhCustomers";
import { listBillingAccounts } from "../../api/payments";
import { listPriceComponents, type PriceComponent } from "../../api/priceList";
import { getIssuedInvoicePdf, listIssuedInvoices } from "../../api/issuedBilling";
import type { AnkhInvoice } from "../../api/ankhInvoices";
import { istDate, pdfFileName, saveBlob } from "./AnkhInvoices";
import { ContactsDialog } from "./Contacts";
import { issuedErrorTitle } from "./IssuedBilling";

const inr = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** radbilling's GST state names (model/gststate.go): only these resolve a place of supply. */
export const GST_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh", "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

function ErrorAlert({ error, what }: { error: Error; what: string }) {
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>{issuedErrorTitle(error, what)}</AlertTitle>
      {error.message}
    </Alert>
  );
}

/** Who an invoice is being raised for: an account and the name to show. */
interface Billable {
  accountid: string;
  name: string;
}

const STATE_CHIP: Record<ReturnType<typeof customerState>, { label: string; color: "default" | "warning" | "info" | "success" }> = {
  "profile incomplete": { label: "Profile incomplete", color: "warning" },
  "no account": { label: "No billing account", color: "default" },
  test: { label: "TEST", color: "info" },
  ready: { label: "LIVE", color: "success" },
};

/**
 * Billing › Customers (owner 2026-10-23): Ankh's own customers -- operators on
 * AnkhOS and cloud customers -- with their billing account, and the one place
 * Ankh raises an invoice by hand. io.ankhos superadmin only; the gateway
 * refuses any other namespace.
 */
export function AnkhCustomersScreen() {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [raiseFor, setRaiseFor] = useState<Billable | null>(null);
  const [historyFor, setHistoryFor] = useState<Billable | null>(null);
  const [contactsFor, setContactsFor] = useState<Billable | null>(null);
  const [addCompany, setAddCompany] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const isSeller = identity.namespace === ANKH_SELLER;
  // Platform viewer reads; the owner, platform admin and platform billing write
  // (radaccess v1.154 ankhcustomer:write). DISPLAY ONLY -- the gateway decides.
  const canWrite = canWriteAnkhCustomers(identity.roles);

  const customers = useQuery({
    queryKey: ["ankh-customers"],
    queryFn: ({ signal }) => listAnkhCustomers(identity, signal),
    enabled: Boolean(identity.token && isSeller),
    retry: false,
  });
  const accounts = useQuery({
    queryKey: ["billing-accounts", ANKH_SELLER],
    queryFn: ({ signal }) => listBillingAccounts(identity, ANKH_SELLER, signal),
    enabled: Boolean(identity.token && isSeller),
    retry: false,
  });

  // Accounts opened by hand for a company with no operator namespace.
  const companies = useMemo(() => {
    const operatorAccounts = new Set((customers.data?.customers ?? []).map((c) => c.accountid));
    return (accounts.data ?? []).filter((a) => !operatorAccounts.has(a.accountid));
  }, [customers.data, accounts.data]);

  const counts = useMemo(() => {
    const c = { live: 0, test: 0, noAccount: 0, incomplete: 0 };
    for (const x of customers.data?.customers ?? []) {
      const s = customerState(x);
      if (s === "ready") c.live++;
      else if (s === "test") c.test++;
      else if (s === "no account") c.noAccount++;
      else c.incomplete++;
    }
    return c;
  }, [customers.data]);

  if (!isSeller) {
    return (
      <Alert severity="info">
        Customers is Ankh&apos;s own customer ledger. Sign in to <code>{ANKH_SELLER}</code> to use it.
      </Alert>
    );
  }

  async function open(c: AnkhCustomer) {
    setNotice(null);
    setActionError(null);
    setOpening(c.namespace);
    try {
      const r = await openOperatorAccount(identity, c.namespace);
      setNotice(`Billing account opened for ${c.legalname}.${r.warning ? " " + r.warning : ""}`);
      await qc.invalidateQueries({ queryKey: ["ankh-customers"] });
      await qc.invalidateQueries({ queryKey: ["billing-accounts", ANKH_SELLER] });
    } catch (e) {
      setActionError(e as Error);
    } finally {
      setOpening(null);
    }
  }

  return (
    <Box sx={{ maxWidth: 1250 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5">Client accounts</Typography>
        {canWrite && <Button variant="contained" onClick={() => setAddCompany(true)}>
          Add company
        </Button>}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ankh&apos;s customers: operators on AnkhOS and cloud customers. Every invoice here is issued by ANKH CLOUD TECH (OPC)
        PRIVATE LIMITED from <code>{ANKH_SELLER}</code>. An operator&apos;s name, GSTIN and address come from its own Edit
        profile; TEST customers get TEST invoices that are never filed.
      </Typography>

      {customers.data && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip color="success" variant="outlined" label={`Live ${counts.live}`} />
          <Chip color="info" variant="outlined" label={`Test ${counts.test}`} />
          <Chip variant="outlined" label={`No account ${counts.noAccount}`} />
          <Chip color="warning" variant="outlined" label={`Profile incomplete ${counts.incomplete}`} />
          <Chip variant="outlined" label={`Companies ${companies.length}`} />
        </Stack>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && <ErrorAlert error={actionError} what="opening the account" />}
      {customers.isLoading && <Typography variant="body2">Loading customers…</Typography>}
      {customers.isError && <ErrorAlert error={customers.error as Error} what="the customer list" />}
      {accounts.isError && <ErrorAlert error={accounts.error as Error} what="Ankh's billing accounts" />}

      {customers.data && (
        <>
          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
            Operators
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small" aria-label="Operators">
              <TableHead>
                <TableRow>
                  <TableCell>Operator</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>GSTIN</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Profile still needs</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.data.customers.map((c) => {
                  const st = STATE_CHIP[customerState(c)];
                  const canOpen = !c.accountopen && c.legalname && c.address && c.state;
                  return (
                    <TableRow key={c.namespace}>
                      <TableCell>
                        <Typography variant="body2">{c.legalname || <em>no legal name</em>}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.namespace}
                          {!c.active && " · inactive"}
                        </Typography>
                      </TableCell>
                      <TableCell>{c.operatortype}</TableCell>
                      <TableCell>{c.gstin || "—"}</TableCell>
                      <TableCell>{c.state || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} color={st.color} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{c.missing.length ? c.missing.join(", ") : "—"}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {c.accountopen ? (
                          <>
                            <Button size="small" onClick={() => setHistoryFor({ accountid: c.accountid, name: c.legalname })}>
                              Invoices
                            </Button>
                            {canWrite && <Button size="small" onClick={() => setContactsFor({ accountid: c.accountid, name: c.legalname })}>
                              Contacts
                            </Button>}
                            {canWrite && <Button size="small" onClick={() => setRaiseFor({ accountid: c.accountid, name: c.legalname })}>
                              Raise invoice
                            </Button>}
                          </>
                        ) : canWrite ? (
                          <Button size="small" disabled={!canOpen || opening === c.namespace} onClick={() => void open(c)}>
                            {opening === c.namespace ? "Opening…" : "Open account"}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Companies
          </Typography>
          {companies.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No company customers yet. Use Add company for a customer that has no operator namespace.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small" aria-label="Companies">
                <TableBody>
                  {companies.map((a) => (
                    <TableRow key={a.accountid}>
                      <TableCell>{a.name || a.accountid}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setHistoryFor({ accountid: a.accountid, name: a.name })}>
                          Invoices
                        </Button>
                        {canWrite && <Button size="small" onClick={() => setContactsFor({ accountid: a.accountid, name: a.name })}>
                          Contacts
                        </Button>}
                        {canWrite && <Button size="small" onClick={() => setRaiseFor({ accountid: a.accountid, name: a.name })}>
                          Raise invoice
                        </Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {addCompany && (
        <AddCompanyDialog
          types={customers.data?.customertypes ?? DEFAULT_CUSTOMER_TYPES}
          onClose={() => setAddCompany(false)}
          onOpened={async (name, warning) => {
            setAddCompany(false);
            setNotice(`Billing account opened for ${name}.${warning ? " " + warning : ""}`);
            await qc.invalidateQueries({ queryKey: ["billing-accounts", ANKH_SELLER] });
          }}
        />
      )}
      {raiseFor && <RaiseInvoiceDialog customer={raiseFor} onClose={() => setRaiseFor(null)} />}
      {historyFor && <HistoryDialog customer={historyFor} onClose={() => setHistoryFor(null)} />}
      {contactsFor && <ContactsDialog accountId={contactsFor.accountid} name={contactsFor.name || contactsFor.accountid} onClose={() => setContactsFor(null)} />}
    </Box>
  );
}

const EMPTY_COMPANY: CompanyAccountInput = { customertype: "", legalname: "", gstin: "", address: "", city: "", state: "", pin: "", email: "", mobile: "" };

function AddCompanyDialog({
  types,
  onClose,
  onOpened,
}: {
  types: AnkhCustomerType[];
  onClose: () => void;
  onOpened: (name: string, warning?: string) => void;
}) {
  const identity = useIdentity();
  const [f, setF] = useState<CompanyAccountInput>({ ...EMPTY_COMPANY, customertype: types[0]?.code ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const set = (k: keyof CompanyAccountInput) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const gstin = f.gstin.trim().toUpperCase();
  const gstinBad = gstin !== "" && !GSTIN_RE.test(gstin);
  const ok = f.customertype && f.legalname.trim() && f.address.trim() && f.state && !gstinBad;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await openCompanyAccount(identity, f);
      onOpened(f.legalname.trim(), r.warning);
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add company</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A customer that buys from Ankh and has no operator namespace. An operator is added from its own row instead.
        </Typography>
        {error && <ErrorAlert error={error} what="opening the account" />}
        <Stack spacing={2}>
          <TextField select required size="small" label="Customer type" value={f.customertype} onChange={set("customertype")}>
            {types.map((t) => (
              <MenuItem key={t.code} value={t.code}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField required size="small" label="Legal name" value={f.legalname} onChange={set("legalname")} />
          <TextField
            size="small"
            label="GSTIN"
            value={f.gstin}
            onChange={set("gstin")}
            error={gstinBad}
            helperText={gstinBad ? "Not a 15-character GSTIN" : "Leave blank for an unregistered customer"}
          />
          <TextField required size="small" label="Billing address" value={f.address} onChange={set("address")} multiline />
          <Stack direction="row" spacing={2}>
            <TextField size="small" label="City" value={f.city} onChange={set("city")} sx={{ flex: 1 }} />
            <TextField size="small" label="PIN" value={f.pin} onChange={set("pin")} sx={{ width: 120 }} />
          </Stack>
          <TextField select required size="small" label="State" helperText="Place of supply" value={f.state} onChange={set("state")}>
            {GST_STATES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField size="small" label="Billing email" value={f.email} onChange={set("email")} sx={{ flex: 1 }} />
            <TextField size="small" label="Mobile" value={f.mobile} onChange={set("mobile")} sx={{ width: 180 }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ok || busy} onClick={() => void submit()}>
          {busy ? "Opening…" : "Open billing account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface LineDraft {
  componentid: string;
  quantity: string;
  unitprice: string;
  discountreason: string;
  note: string;
}

const EMPTY_LINE: LineDraft = { componentid: "", quantity: "1", unitprice: "", discountreason: "", note: "" };

/** Period buttons: the end date for a period of N months from "Period from". */
const PERIOD_PRESETS: [string, number][] = [
  ["Quarter", 3],
  ["Half-year", 6],
  ["Year", 12],
];

const today = () => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
const plusDays = (day: string, n: number) => new Date(new Date(`${day}T00:00:00Z`).getTime() + n * 86400_000).toISOString().slice(0, 10);

/**
 * Raise invoice: period and items -> a DRAFT priced by radbilling -> preview ->
 * Issue, which assigns the next number. Nothing is numbered until Issue.
 */
function RaiseInvoiceDialog({ customer, onClose }: { customer: Billable; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(plusDays(today(), 30));
  const [lines, setLines] = useState<LineDraft[]>([EMPTY_LINE]);
  const [draft, setDraft] = useState<AnkhInvoice | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const components = useQuery({
    queryKey: ["price-components", ANKH_SELLER],
    queryFn: ({ signal }) => listPriceComponents(identity, ANKH_SELLER, signal),
    enabled: Boolean(identity.token),
    retry: false,
  });
  const usable: PriceComponent[] = (components.data ?? []).filter((c) => c.active && !c.pureinternetservice);

  const listPrice = (id: string) => usable.find((c) => c.id === id)?.price;
  const linesOk =
    lines.length > 0 &&
    lines.every(
      (l) =>
        l.componentid &&
        Number(l.quantity) > 0 &&
        (l.unitprice === "" || Number(l.unitprice) >= 0) &&
        (!priceNeedsReason(l.unitprice, listPrice(l.componentid)) || l.discountreason.trim()) &&
        l.note.trim().length <= MAX_LINE_NOTE,
    );
  const periodOk = start && end && end > start;

  async function makeDraft() {
    setBusy("Creating the draft…");
    setError(null);
    try {
      const inv = await createDraftInvoice(identity, customer.accountid, start, end);
      for (const l of lines) {
        await addInvoiceLine(identity, customer.accountid, inv.id, {
          componentid: l.componentid,
          quantity: Number(l.quantity),
          ...(l.unitprice !== "" ? { unitprice: Number(l.unitprice) } : {}),
          ...(priceNeedsReason(l.unitprice, listPrice(l.componentid)) ? { discountreason: l.discountreason.trim() } : {}),
          ...(l.note.trim() ? { note: l.note.trim() } : {}),
        });
      }
      setDraft(await getAnkhInvoice(identity, customer.accountid, inv.id));
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(null);
    }
  }

  async function issue() {
    if (!draft) return;
    setBusy("Issuing…");
    setError(null);
    try {
      await setInvoiceStatus(identity, customer.accountid, draft.id, "Generated");
      setDraft(await getAnkhInvoice(identity, customer.accountid, draft.id));
      await qc.invalidateQueries({ queryKey: ["issued-invoices", ANKH_SELLER, customer.accountid] });
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(null);
    }
  }

  async function discard() {
    if (!draft) return onClose();
    setBusy("Discarding…");
    try {
      await setInvoiceStatus(identity, customer.accountid, draft.id, "Cancelled");
      onClose();
    } catch (e) {
      setError(e as Error);
      setBusy(null);
    }
  }

  async function pdf() {
    if (!draft) return;
    try {
      saveBlob(await getIssuedInvoicePdf(identity, ANKH_SELLER, customer.accountid, draft.id), pdfFileName(draft));
    } catch (e) {
      setError(e as Error);
    }
  }

  const issued = Boolean(draft?.invoicenumber);
  const setLine = (i: number, k: keyof LineDraft, v: string) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Raise invoice — {customer.name || customer.accountid}</DialogTitle>
      <DialogContent>
        {error && <ErrorAlert error={error} what="the invoice" />}
        {components.isError && <ErrorAlert error={components.error as Error} what="Ankh's price list" />}

        {!draft && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField type="date" size="small" label="Period from" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField type="date" size="small" label="Period to" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} error={!periodOk} />
              {PERIOD_PRESETS.map(([label, months]) => (
                <Button key={label} size="small" disabled={!start} onClick={() => setEnd(periodEnd(start, months))}>
                  {label}
                </Button>
              ))}
            </Stack>
            {lines.map((l, i) => {
              const comp = usable.find((c) => c.id === l.componentid);
              return (
                <Stack key={i} spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField select size="small" label="Item" value={l.componentid} onChange={(e) => setLine(i, "componentid", e.target.value)} sx={{ flex: 1 }}>
                      {usable.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name} — ₹{inr(c.price)} + {c.gstpercent}% GST
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField size="small" label="Qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} sx={{ width: 80 }} />
                    <TextField
                      size="small"
                      label="Price (net)"
                      placeholder={comp ? inr(comp.price) : ""}
                      helperText="Blank = price list"
                      value={l.unitprice}
                      onChange={(e) => setLine(i, "unitprice", e.target.value)}
                      sx={{ width: 140 }}
                    />
                    {priceNeedsReason(l.unitprice, comp?.price) && (
                      <TextField size="small" required label="Reason" value={l.discountreason} onChange={(e) => setLine(i, "discountreason", e.target.value)} sx={{ width: 180 }} />
                    )}
                    <IconButton aria-label="Remove item" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                      ×
                    </IconButton>
                  </Stack>
                  <TextField
                    size="small"
                    label="Note on the invoice (optional)"
                    placeholder="e.g. 2 servers @ Rs. 40,000 per server per year. From 01 Oct 2026 to 31 Mar 2027"
                    value={l.note}
                    onChange={(e) => setLine(i, "note", e.target.value)}
                    error={l.note.trim().length > MAX_LINE_NOTE}
                    helperText={`${l.note.trim().length}/${MAX_LINE_NOTE}`}
                  />
                </Stack>
              );
            })}
            <Box>
              <Button size="small" onClick={() => setLines([...lines, EMPTY_LINE])}>
                + Add item
              </Button>
            </Box>
          </Stack>
        )}

        {draft && (
          <Box sx={{ mt: 1 }}>
            {issued ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Issued as <strong>{draft.invoicenumber}</strong>. The PDF has been stored and will be emailed to the customer.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Draft — check it. <strong>Issue</strong> assigns the next number and cannot be undone; only a credit note reverses an
                issued invoice.
              </Alert>
            )}
            <Typography variant="subtitle2">{draft.documentheading}</Typography>
            <Typography variant="body2">
              {draft.supplierlegalname} → {draft.buyername}
              {draft.buyergstin ? ` (GSTIN ${draft.buyergstin})` : " (unregistered)"} · Place of supply {draft.placeofsupply} · {istDate(draft.periodstart)} –{" "}
              {istDate(draft.periodend)}
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small" aria-label="Invoice lines">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>SAC</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Taxable ₹</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Total ₹</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(draft.lineitems ?? []).map((li) => (
                    <TableRow key={li.id}>
                      <TableCell>{li.description}</TableCell>
                      <TableCell>{li.hsnsac ?? ""}</TableCell>
                      <TableCell align="right">{li.quantity}</TableCell>
                      <TableCell align="right">{inr(li.taxablevalue)}</TableCell>
                      <TableCell align="right">
                        {li.gstpercent}% · {inr(li.taxamount)}
                      </TableCell>
                      <TableCell align="right">{inr(li.totalamount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3}>
                      <strong>Total</strong> — CGST {inr(draft.totalcgst)} · SGST {inr(draft.totalsgst)} · IGST {inr(draft.totaligst)}
                    </TableCell>
                    <TableCell align="right">{inr(draft.taxablevalue)}</TableCell>
                    <TableCell align="right">{inr(draft.totaltax)}</TableCell>
                    <TableCell align="right">
                      <strong>{inr(draft.totalamount)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption">{draft.totalamountinwords}</Typography>
          </Box>
        )}
        {busy && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            {busy}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {!draft && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!linesOk || !periodOk || Boolean(busy)} onClick={() => void makeDraft()}>
              Create draft
            </Button>
          </>
        )}
        {draft && !issued && (
          <>
            <Button color="inherit" disabled={Boolean(busy)} onClick={() => void discard()}>
              Discard draft
            </Button>
            <Button disabled={Boolean(busy)} onClick={() => void pdf()}>
              Download draft PDF
            </Button>
            <Button variant="contained" disabled={Boolean(busy)} onClick={() => void issue()}>
              Issue invoice
            </Button>
          </>
        )}
        {draft && issued && (
          <>
            <Button onClick={() => void pdf()}>Download PDF</Button>
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function HistoryDialog({ customer, onClose }: { customer: Billable; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [error, setError] = useState<Error | null>(null);
  const [toIssue, setToIssue] = useState<AnkhInvoice | null>(null);
  const [issuing, setIssuing] = useState(false);

  // A draft agreed with the customer is issued from here: Issue assigns the
  // next number and cannot be undone (owner 2026-11-04, Quest: draft first).
  async function issue(inv: AnkhInvoice) {
    setIssuing(true);
    setError(null);
    try {
      await setInvoiceStatus(identity, customer.accountid, inv.id, "Generated");
      setToIssue(null);
      await qc.invalidateQueries({ queryKey: ["issued-invoices", ANKH_SELLER, customer.accountid] });
    } catch (e) {
      setError(e as Error);
    } finally {
      setIssuing(false);
    }
  }
  const list = useQuery({
    queryKey: ["issued-invoices", ANKH_SELLER, customer.accountid],
    queryFn: ({ signal }) => listIssuedInvoices(identity, ANKH_SELLER, customer.accountid, signal),
    enabled: Boolean(identity.token),
    retry: false,
  });

  async function pdf(inv: AnkhInvoice) {
    try {
      saveBlob(await getIssuedInvoicePdf(identity, ANKH_SELLER, customer.accountid, inv.id), pdfFileName(inv));
    } catch (e) {
      setError(e as Error);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Invoices — {customer.name || customer.accountid}</DialogTitle>
      <DialogContent>
        {error && <ErrorAlert error={error} what="the PDF" />}
        {list.isLoading && <Typography variant="body2">Loading…</Typography>}
        {list.isError && <ErrorAlert error={list.error as Error} what="the invoice list" />}
        {list.data && list.data.length === 0 && <Alert severity="info">No invoices yet.</Alert>}
        {list.data && list.data.length > 0 && (
          <TableContainer component={Paper}>
            <Table size="small" aria-label="Customer invoices">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Amount ₹</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {list.data.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{inv.invoicenumber || <em>Draft</em>}</TableCell>
                    <TableCell>{istDate(inv.createdat)}</TableCell>
                    <TableCell>
                      {istDate(inv.periodstart)} – {istDate(inv.periodend)}
                    </TableCell>
                    <TableCell align="right">{inr(inv.totalamount)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={inv.syncstatus} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      {isOpenDraft(inv) && (
                        <Button size="small" onClick={() => setToIssue(inv)}>
                          Issue
                        </Button>
                      )}
                      <Button size="small" disabled={!inv.invoicenumber && !isOpenDraft(inv)} onClick={() => void pdf(inv)}>
                        {isOpenDraft(inv) ? "Draft PDF" : "PDF"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {toIssue && (
        <Dialog open onClose={issuing ? undefined : () => setToIssue(null)}>
          <DialogTitle>Issue this invoice?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {istDate(toIssue.periodstart)} – {istDate(toIssue.periodend)} · ₹{inr(toIssue.totalamount)}. Issue assigns the next invoice
              number, stores the PDF and emails it to the customer. It cannot be undone; only a credit note reverses an issued invoice.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={issuing} onClick={() => setToIssue(null)}>
              Cancel
            </Button>
            <Button variant="contained" disabled={issuing} onClick={() => void issue(toIssue)}>
              Issue invoice
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}

/** A draft not yet numbered or discarded: it can be previewed and issued. */
function isOpenDraft(inv: Pick<AnkhInvoice, "invoicenumber" | "syncstatus">): boolean {
  return !inv.invoicenumber && inv.syncstatus === "Draft";
}
